import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CloudDownloadOutlined from "@mui/icons-material/CloudDownloadOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { FarmVaultIcon } from "components/Icons/Icons";
import { useUser } from "components/Menu/hooks/useUser";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { USER_ROLE_ADMIN } from "core/useSession";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";
import { useSnackbar } from "notistack";
import { v4 } from "uuid";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import FarmCountyCard from "./components/FarmCountyCard";
import FarmCountyDataTable from "./components/FarmCountyDataTable";
import { FarmEventList } from "./components/FarmEventList";
import ImportUploadFileDialog from "./components/Import/ImportUploadFileDialog";
import ProducerFarmCopyAipPassDialog from "./components/Import/ProducerFarmCopyAipPassDialog";
import ProducerFarmCopyDialog from "./components/Import/ProducerFarmCopyDialog";
import { exportFarms578Workbook } from "./exportFarms578Workbook";
import { getUniqueCounties } from "./helpers";
import { ProducerFarmFieldCropDialog } from "./ProducerFarmFieldCropDialog";
import { useFarmController } from "./useFarmController";

export default function VaultCurrentWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const { addAttachments } = useChatAssistantAttachments();
  const { myUser } = useUser(
    {
      user_role: globalState?.user_role,
      producer_token: globalState?.producer_token,
      agent_token: globalState?.agent_token,
      globalDispatch: globalState?.dispatch,
    },
    null
  );

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();

  const CARD_GRID_KEY = "farm_vault";
  const [workspaceMode, setWorkspaceMode] = useState(null);

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  const [isExporting, setIsExporting] = useState(false);
  const handleExportClick = async () => {
    setIsExporting(true);
    try {
      await exportFarms578Workbook(globalState, currentYear, producerFarmFieldCrops, enqueueSnackbar);
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const [isCopyYearDialogOpen, setIsCopyYearDialogOpen] = useState(false);
  const handleCopyYearDialogClose = (_crop_year) => {
    setIsCopyYearDialogOpen(false);
    if (_crop_year && _crop_year !== currentYear) {
      setCurrentYear(_crop_year);
    } else {
      loadCurrent();
    }
  };

  const [isCopyAipPassDialogOpen, setIsCopyAipPassDialogOpen] = useState(false);
  const handleCopyAipPassDialogClose = (_crop_year) => {
    setIsCopyAipPassDialogOpen(false);
    if (_crop_year) {
      setCurrentYear(_crop_year);
    }
    loadCurrent();
  };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [isUploadFileDialogOpen, setIsUploadFileDialogOpen] = useState(false);

  const { component: confirmDialogComponent } = useDialog();

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // --- --- ---

  const {
    isLoadingProducerFarmFieldCropEvents,
    producerFarmFieldCropEvents,
    _loadProducerFarmFieldCropEvents,
    producerFarmFieldCrops,
  } = useFarmController({
    crop_year: currentYear,
    filter: null,
    doLoadProducerFarmFieldCropEvents: true /* Load Events */,
    onError: handleError,
  });

  const handleOnChangeRecords_EventItem = async (records) => {
    if (records?.length) {
      const _eventItem = records[0];

      _eventItem.id = v4();
      _eventItem.producer_token = globalState.producer_token;
      _eventItem.crop_year = currentYear;
      _eventItem.is_active = true;
      _eventItem.year = currentYear;
      if (!_eventItem.module) _eventItem.module = "Other";
      if (!_eventItem.category) _eventItem.category = "Other";

      await UsersApiHelper.updateProducerFarmFieldCropEvent(_eventItem);
      _loadProducerFarmFieldCropEvents();
    }
  };

  // ---

  const [isLoadingCurrent, setIsLoadingCurrent] = useState(true);
  const { state: currentCards, actions: currentActions } = useCardController();
  const loadCurrent = async () => {
    try {
      setIsLoadingCurrent(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: currentYear,
        is_active: true,
      };
      const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
      const countyPractices = getUniqueCounties(farms);
      currentActions.replace(countyPractices);

      setIsLoadingCurrent(false);
    } catch (err) {
      setIsLoadingCurrent(false);
      currentActions.replace([]);
      handleError(err);
    }
  };

  // Update attachments whenever producerFarmFieldCrops data changes
  useEffect(() => {
    const attachments = [];

    if (producerFarmFieldCrops && producerFarmFieldCrops.length > 0) {
      attachments.push({ name: "Producer Farm Field Crops", content: producerFarmFieldCrops });
    }

    addAttachments(attachments);
  }, [producerFarmFieldCrops, addAttachments]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadCurrent();
    }
  }, [currentYear, globalState?.producer_token]);

  // Cleanup effect to clear attachments when component unmounts
  useEffect(() => {
    return () => {
      addAttachments([]);
    };
  }, []); // Empty dependency array since we use ref

  const handlePrintClick = () => {
    navigate(`./print/${currentYear}`);
  };

  // ---

  const [isAddCropDialogOpen, setIsAddCropDialogOpen] = useState(false);

  const anchor = "right";

  const [state, setState] = useState({
    right: false,
  });

  const toggleDrawer = (anchor, open) => () => {
    setState({ ...state, [anchor]: open });
  };

  const FarmEventsButton = ({ onClick }) => (
    <Button className="farm-event-btn" variant="contained" onClick={onClick}>
      <VisibilityOutlined sx={{ mr: 1 }} />
      Farm Events
    </Button>
  );

  return (
    <>
      <WorkspaceHeader
        title="Planted Acres"
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
      />

      <Grid container>
        <Grid item xs={12} lg={12}>
          <WorkspacePageHeader
            currentYear={currentYear}
            onYearChange={setCurrentYear}
            viewMode={workspaceMode}
            onViewModeChange={setWorkspaceMode}
            cardGridKey={CARD_GRID_KEY}
            isLoading={isLoadingCurrent}
            additionalControls={<FarmEventsButton onClick={toggleDrawer(anchor, true)} />}
          />

          {workspaceMode === WORKSPACE_VIEW_CARD && (
            <Box className="cardContainer">
              {currentCards.map((currentCountyPractice, index) => (
                <FarmCountyCard currentCounty={currentCountyPractice} crop_year={currentYear} key={index} />
              ))}
              {!currentCards?.length && (
                <EmptyCard
                  text="No farms listed"
                  secondaryText="Get started by importing history or adding a farm"
                  isLoading={isLoadingCurrent}
                />
              )}
            </Box>
          )}

          {workspaceMode === WORKSPACE_VIEW_TABLE && (
            <FarmCountyDataTable currentCards={currentCards} crop_year={currentYear} isLoading={isLoadingCurrent} />
          )}
        </Grid>

        {globalState?.user_role === USER_ROLE_ADMIN && isMobile && <Divider flexItem />}
        <Drawer
          anchor={anchor}
          open={state[anchor]}
          onClose={toggleDrawer(anchor, false)}
          className="farm-event-drawer"
        >
          <Stack
            direction="row"
            spacing={2}
            className="headerWithMenuButton"
            sx={{
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h1">Farm Events</Typography>
              <ChatAssistantButton
                systemPrompt={SYSTEM_PROMPT_FARM_EVENTS}
                onChangeRecords={handleOnChangeRecords_EventItem}
              />
            </Stack>

            {isLoadingProducerFarmFieldCropEvents && <CircularProgress />}

            <CloseOutlined className="drawer-close-btn" sx={{ mr: 1 }} onClick={toggleDrawer(anchor, false)} />
          </Stack>
          <Divider />
          <FarmEventList producerFarmFieldCropEvents={producerFarmFieldCropEvents} />
        </Drawer>
      </Grid>

      <WorkspaceActionBar
        content={
          <>
            <Button variant="contained" onClick={() => setIsAddCropDialogOpen(true)}>
              <AddCircleOutline sx={{ mr: 1 }} />
              Add {!isSmall && " Crop"}
            </Button>
            <Button variant="contained" onClick={handleExportClick} disabled={isExporting}>
              {isExporting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CloudDownloadOutlined sx={{ mr: 1 }} />}
              Export
            </Button>
            {/* {[USER_ROLE_ADMIN, USER_ROLE_AGENT].includes(globalState?.user_role) && */}
            <PopupState variant="popover" popupId="demo-popup-menu">
              {(popupState) => (
                <>
                  <Button variant="contained" {...bindTrigger(popupState)}>
                    <ArrowDropDownOutlined sx={{ mr: 1 }} />
                    Import
                  </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={() => {
                        setIsCopyYearDialogOpen(true);
                        popupState.close();
                      }}
                    >
                      <ContentCopyOutlined sx={{ mr: 1 }} /> Copy Prior Season ...
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setIsUploadFileDialogOpen(true);
                        popupState.close();
                      }}
                    >
                      <UploadFileOutlined sx={{ mr: 1 }} /> Upload file ...
                    </MenuItem>

                    <Divider />

                    <MenuItem
                      onClick={() => {
                        setIsCopyAipPassDialogOpen(true);
                        popupState.close();
                      }}
                    >
                      <CloudDownloadOutlined sx={{ mr: 1 }} /> Import from History ...
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>

            <Button variant="contained" onClick={handlePrintClick}>
              <PrintOutlined sx={{ mr: 1 }} /> Print
            </Button>
          </>
        }
      />

      {isAddCropDialogOpen && (
        <Dialog open={isAddCropDialogOpen} PaperProps={{ sx: { minHeight: "5in" } }}>
          <ProducerFarmFieldCropDialog
            crop_year={currentYear}
            location_state_name={null}
            location_county_name={null}
            onClose={() => {
              setIsAddCropDialogOpen(false);
              loadCurrent();
            }}
          />
        </Dialog>
      )}

      {isCopyYearDialogOpen && (
        <Dialog open={isCopyYearDialogOpen} fullWidth={isSmall} PaperProps={{ sx: { minHeight: "80vh" } }}>
          <ProducerFarmCopyDialog onClose={handleCopyYearDialogClose} />
        </Dialog>
      )}

      {isUploadFileDialogOpen && (
        <Dialog open={isUploadFileDialogOpen} fullWidth={isSmall} PaperProps={{ sx: {} }}>
          <ImportUploadFileDialog
            year={currentYear}
            onClose={() => {
              setIsUploadFileDialogOpen(false);
              loadCurrent();
            }}
          />
        </Dialog>
      )}

      {isCopyAipPassDialogOpen && (
        <Dialog open={isCopyAipPassDialogOpen} fullWidth={isSmall} PaperProps={{ sx: { minHeight: "80vh" } }}>
          <ProducerFarmCopyAipPassDialog onClose={handleCopyAipPassDialogClose} />
        </Dialog>
      )}

      {confirmDialogComponent}
    </>
  );
}

export const SYSTEM_PROMPT_FARM_EVENTS = "form_farm_events";
